import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import { LoteCommonHeader } from "./lote/LoteCommonHeader";
import { LoteRowsTable } from "./lote/LoteRowsTable";
import {
  emptyLoteRow, initialCommon, filterValidRows,
  applyRowUpdate, buildEntregaPayload, computeDuplicateInfo, dupWindowStart,
} from "./lote/loteHelpers";
import { getMissingRequiredFields, buildErrorMessage } from "@/pages/entregas/entregasValidation";
import { WhatsAppLoteSummary } from "./lote/WhatsAppLoteSummary";

// Dialog de criação de entregas em lote (bulk).
// Casca fina: helpers puros em `loteHelpers`, sub-componentes em `LoteCommonHeader` e `LoteRowsTable`.
// fev/2026: Tipo e Forma de Pagamento migraram do cabeçalho para cada linha da tabela.
// fev/2026 (v3): botões "+" de cadastro rápido por linha + destaque de duplicidade.
// fev/2026 (v4): duplicidade agora considera STATUS da entrega existente:
//   - BLOCKING (aguardando/em_transito/concluida) → TRAVA salvamento (vermelho)
//   - WARN-ONLY (remarcar/cancelada) → apenas alerta amarelo, permite salvar
//   Além disso, o dialog BUSCA entregas do DB para common.data (o `items` passado
//   por Entregas.jsx traz só o intervalo em vista — poderia deixar duplicidades
//   escaparem em outras datas).
export function LoteDialog({
  open, onOpenChange, entregadores, tipos, formas, bairros, regioes, rotas = [],
  user, onCatalogAdd, items, onCreated,
}) {
  const [common, setCommon] = useState(initialCommon());
  const [rows, setRows] = useState([emptyLoteRow(), emptyLoteRow(), emptyLoteRow()]);
  const [saving, setSaving] = useState(false);
  // Fonte da verdade pra duplicidade: entregas do DB filtradas por common.data.
  const [dbForDate, setDbForDate] = useState([]);

  // Refetch sempre que o modal abre ou common.data muda.
  // fev/2026 (v5): busca últimos 30 dias (não só a mesma data), para pegar
  // duplicatas em datas anteriores. `end` fica em aberto pra pegar entregas
  // futuras já agendadas com o mesmo pedido.
  useEffect(() => {
    if (!open || !common.data) return;
    let cancelled = false;
    const start = dupWindowStart(common.data);
    api.get(`/entregas?start=${start}`)
      .then((r) => { if (!cancelled) setDbForDate(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setDbForDate([]); });
    return () => { cancelled = true; };
  }, [open, common.data]);

  // Fallback: se dbForDate ainda não carregou, usa items do prop.
  const dupSource = useMemo(
    () => (dbForDate.length > 0 ? dbForDate : (items || [])),
    [dbForDate, items],
  );

  const validRows = filterValidRows(rows);
  const duplicateInfo = useMemo(
    () => computeDuplicateInfo(rows, dupSource, common.data),
    [rows, dupSource, common.data],
  );
  // Se ANY linha tem blocking=true, desabilita o botão salvar
  const hasBlockingDup = useMemo(
    () => Array.from(duplicateInfo.values()).some((d) => d.blocking),
    [duplicateInfo],
  );
  // fev/2026: linhas com conteúdo mas faltando obrigatórios travam o salvar.
  // Aplica mesma regra do formulário individual, e destaca visualmente no
  // LoteRowsTable via getMissingRequiredFields.
  const incompleteRowsCount = useMemo(
    () => validRows.filter((r) => getMissingRequiredFields(r, tipos).size > 0).length,
    [validRows, tipos],
  );
  const firstMissing = useMemo(() => {
    for (const r of validRows) {
      const m = getMissingRequiredFields(r, tipos);
      if (m.size > 0) return { missing: m, row: r };
    }
    return null;
  }, [validRows, tipos]);

  const updateRow = (i, field, val) => setRows((prev) => applyRowUpdate(prev, i, field, val, bairros, formas));
  const addRow = () => setRows((prev) => [...prev, emptyLoteRow()]);
  const removeRow = (i) => setRows((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const submit = async () => {
    if (validRows.length === 0) return toast.error("Adicione pelo menos uma linha válida");
    if (incompleteRowsCount > 0) {
      return toast.error(
        `${incompleteRowsCount} linha(s) com campos obrigatórios em branco — corrija os itens em vermelho.`
      );
    }
    if (hasBlockingDup) return toast.error("Existem linhas com número de pedido duplicado — corrija antes de salvar.");
    const entregas = validRows.map((r) => buildEntregaPayload(r, common, bairros, tipos));
    setSaving(true);
    try {
      const r = await api.post("/entregas/bulk-create", { entregas });
      toast.success(`${r.data.created} entrega(s) criadas em lote`);
      onOpenChange(false);
      setRows([emptyLoteRow(), emptyLoteRow(), emptyLoteRow()]);
      setCommon(initialCommon());
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao criar em lote");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] max-w-none sm:max-w-none max-h-[94vh] overflow-y-auto" data-testid="dialog-lote">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-orange-600" />Adicionar Entregas em Lote
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <LoteCommonHeader common={common} setCommon={setCommon} entregadores={entregadores} />
          <LoteRowsTable rows={rows} updateRow={updateRow} addRow={addRow} removeRow={removeRow}
            bairros={bairros} tipos={tipos} formas={formas} regioes={regioes} rotas={rotas}
            user={user} onCatalogAdd={onCatalogAdd} duplicateInfo={duplicateInfo} />
          <WhatsAppLoteSummary rows={rows} />
          <div className="flex gap-2 justify-end pt-2 border-t items-center">
            {incompleteRowsCount > 0 && (
              <div
                className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-1.5 mr-auto"
                data-testid="lote-missing-required"
              >
                {incompleteRowsCount} linha(s) faltando obrigatórios
                {firstMissing && ` — ex: ${buildErrorMessage(firstMissing.missing, firstMissing.row).replace("Preencha os campos obrigatórios: ", "").replace("Preencha o campo obrigatório: ", "")}`}
              </div>
            )}
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={saving || hasBlockingDup || incompleteRowsCount > 0}
              className={(hasBlockingDup || incompleteRowsCount > 0) ? "bg-slate-400 hover:bg-slate-400 cursor-not-allowed" : "bg-orange-600 hover:bg-orange-700"}
              data-testid="btn-lote-salvar"
            >
              {saving
                ? "Salvando..."
                : hasBlockingDup
                ? "Corrija duplicidades"
                : incompleteRowsCount > 0
                ? `Preencha ${incompleteRowsCount} linha(s)`
                : `Salvar ${validRows.length} entrega(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
